import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Wrench, Globe } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

function LangSwitch() {
  const { lang, setLang } = useI18n();
  return (
    <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-white p-0.5" data-testid="lang-switch">
      {["nl", "fr"].map((l) => (
        <button
          key={l}
          data-testid={`lang-switch-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${
            lang === l ? "bg-[#0F172A] text-white" : "text-slate-500 hover:text-slate-900"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export function Brand({ light = false }) {
  return (
    <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-orange text-white">
        <Wrench size={18} strokeWidth={2.5} />
      </span>
      <span className={`font-heading text-lg font-extrabold tracking-tight ${light ? "text-white" : "text-[#0F172A]"}`}>
        RamenHersteller<span className="text-brand-orange">.be</span>
      </span>
    </Link>
  );
}

export function PublicHeader() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const dashHref = user && user.role === "admin" ? "/admin" : user && user.role === "partner" ? "/partner" : "/account";

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between">
        <Brand />
        <nav className="hidden lg:flex items-center gap-7 text-sm font-medium text-slate-600">
          <a href="/#hoe-werkt-het" className="hover:text-slate-900 transition-colors" data-testid="nav-how">{t("nav.how")}</a>
          <a href="/#herstellingen" className="hover:text-slate-900 transition-colors" data-testid="nav-repairs">{t("nav.repairs")}</a>
          <Link to="/faq" className="hover:text-slate-900 transition-colors" data-testid="nav-faq">{t("nav.faq")}</Link>
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <LangSwitch />
          {user ? (
            <>
              <Link to={dashHref}>
                <Button variant="ghost" size="sm" data-testid="nav-dashboard">
                  {user.role === "admin" ? t("nav.admin") : user.role === "partner" ? t("nav.partner") : t("nav.account")}
                </Button>
              </Link>
              <Button variant="outline" size="sm" onClick={logout} data-testid="nav-logout">{t("nav.logout")}</Button>
            </>
          ) : (
            <Link to="/login">
              <Button variant="ghost" size="sm" data-testid="nav-login">{t("nav.login")}</Button>
            </Link>
          )}
          <Button className="cta-orange rounded-full px-5" onClick={() => navigate("/boeken")} data-testid="nav-book-btn">
            {t("nav.book")}
          </Button>
        </div>
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-3" data-testid="mobile-menu">
          <a href="/#hoe-werkt-het" onClick={() => setOpen(false)} className="block py-1 text-slate-700">{t("nav.how")}</a>
          <a href="/#herstellingen" onClick={() => setOpen(false)} className="block py-1 text-slate-700">{t("nav.repairs")}</a>
          <Link to="/faq" onClick={() => setOpen(false)} className="block py-1 text-slate-700">{t("nav.faq")}</Link>
          <div className="flex items-center justify-between pt-2">
            <LangSwitch />
            {user ? (
              <div className="flex gap-2">
                <Link to={dashHref} onClick={() => setOpen(false)}><Button variant="outline" size="sm">{t("nav.account")}</Button></Link>
                <Button variant="outline" size="sm" onClick={() => { logout(); setOpen(false); }}>{t("nav.logout")}</Button>
              </div>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)}><Button variant="outline" size="sm">{t("nav.login")}</Button></Link>
            )}
          </div>
          <Button className="cta-orange w-full rounded-full" onClick={() => { navigate("/boeken"); setOpen(false); }} data-testid="mobile-book-btn">
            {t("nav.book")}
          </Button>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { t } = useI18n();
  return (
    <footer className="bg-[#0F172A] text-slate-300 mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <Brand light />
          <p className="mt-4 max-w-sm text-sm text-slate-400">{t("footer.tagline")}</p>
          <p className="mt-4 text-sm font-semibold text-brand-orange">{t("value.prop")}</p>
        </div>
        <div>
          <h4 className="font-heading font-semibold text-white mb-3">{t("footer.company")}</h4>
          <ul className="space-y-2 text-sm">
            <li><a href="/#hoe-werkt-het" className="hover:text-white">{t("nav.how")}</a></li>
            <li><a href="/#herstellingen" className="hover:text-white">{t("nav.repairs")}</a></li>
            <li><Link to="/faq" className="hover:text-white">{t("nav.faq")}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-heading font-semibold text-white mb-3">{t("footer.legal")}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">{t("footer.privacy")}</Link></li>
            <li><Link to="/cookies" className="hover:text-white" data-testid="footer-cookies">{t("footer.cookies")}</Link></li>
            <li><Link to="/voorwaarden" className="hover:text-white" data-testid="footer-terms">{t("footer.terms")}</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} RamenHersteller.be — {t("footer.rights")}
      </div>
    </footer>
  );
}

export function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
